import React, { useEffect, useState } from "react";
import ResellerPresenter from "./ResellerPresenter";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { LOGIN_STEP, LoginInfo } from "../LoginContainer";
import { postCheckReseller } from "api/authAPI";
import useStatusMutation from "hook/useStatusMutation";
import {
  PostCheckResellerResponse,
  RESELLER_CHECK_STEP,
  ResellerInfo,
  RESSELLER_USER_TYPE,
} from "api/interfaces/authInterface.interface";
import { useLocation, useNavigate } from "react-router-dom";
import useApiError from "hook/useApiError";

interface Props {
  loginInfo: LoginInfo;
  isValidated: Validate;
  validateMsg: ValidateMsg;
  onChangeStep: (step: LOGIN_STEP) => void;
  onChangeLoginInfo: (e: React.ChangeEvent<HTMLInputElement>) => void;
  validateEmail: () => boolean;
  setIsValidated: (isValidated: Validate) => void;
  setValidateMsg: (validateMsg: ValidateMsg) => void;
}

export default function ResellerContainer({
  loginInfo,
  isValidated,
  validateMsg,
  onChangeStep,
  onChangeLoginInfo,
  validateEmail,
  setIsValidated,
  setValidateMsg,
}: Props) {
  const { displayError, lang } = useApiError();
  const location = useLocation();
  const [type, setType] = useState<RESELLER_CHECK_STEP>(
    RESELLER_CHECK_STEP.CHECK_EMAIL
  );
  const [userType, setUserType] = useState<RESSELLER_USER_TYPE>(
    RESSELLER_USER_TYPE.SALESFORCE_USER
  );
  const [resellerInfo, setResellerInfo] = useState<ResellerInfo>({
    companyName: "",
    name: "",
    cppNo: "",
  });
  const navigate = useNavigate();
  const mutationCheckResellerEmail = useStatusMutation(postCheckReseller, {
    onSuccess: (res: PostCheckResellerResponse) => {
      if (!res.result || res.error !== 0) {
        throw new Error(res.errorString);
      }
      if (res.result.isMyDW) {
        setUserType(RESSELLER_USER_TYPE.MYDW_USER);
      } else if (res.result.hasOrg && res.result.isSalesforce) {
        setUserType(RESSELLER_USER_TYPE.HAS_ORG);
      } else if (res.result.isSalesforce) {
        setUserType(RESSELLER_USER_TYPE.SALESFORCE_USER);
      } else {
        setUserType(RESSELLER_USER_TYPE.NOT_SALESFORCE_USER);
      }
      setResellerInfo({
        companyName: res.result.companyName,
        name: res.result.name,
        cppNo: res.result.cppNo,
      });
      setType(RESELLER_CHECK_STEP.CHECKED_EMAIL);
    },
    onError: (err: CustomError) => {
      console.log(err);
      setIsValidated({ ...isValidated, email: true });
      setValidateMsg({
        ...validateMsg,
        email: displayError(
          err,
          lang.validateMsg.signup.defaultError,
          "validateMsg.signup.reseller"
        ),
      });
    },
  });
  const handleClick = () => {
    if (!mutationCheckResellerEmail.idle) return;
    const result = validateEmail();
    if (!result) return;
    mutationCheckResellerEmail.mutate({
      email: loginInfo.email,
    });
  };
  const onClickSignUp = () => {
    switch (userType) {
      case RESSELLER_USER_TYPE.NOT_SALESFORCE_USER:
        navigate("/reseller", {
          state: {
            email: loginInfo.email,
            userType: RESSELLER_USER_TYPE.NOT_SALESFORCE_USER,
          },
        });
        break;
      case RESSELLER_USER_TYPE.SALESFORCE_USER:
        navigate("/reseller", {
          state: {
            email: loginInfo.email,
            userType: RESSELLER_USER_TYPE.SALESFORCE_USER,
            companyName: resellerInfo.companyName,
            name: resellerInfo.name,
          },
        });
        break;
      case RESSELLER_USER_TYPE.MYDW_USER:
        onChangeStep(LOGIN_STEP.INPUT_EMAIL);
        break;
      case RESSELLER_USER_TYPE.HAS_ORG:
        onChangeStep(LOGIN_STEP.INPUT_EMAIL);
        break;
      default:
        break;
    }
  };
  const onClickGoBack = () => {
    if (type === RESELLER_CHECK_STEP.CHECKED_EMAIL) {
      setType(RESELLER_CHECK_STEP.CHECK_EMAIL);
    } else {
      onChangeStep(LOGIN_STEP.INPUT_EMAIL);
    }
  };
  useEffect(() => {
    if (location.state?.userType) {
      setType(RESELLER_CHECK_STEP.CHECKED_EMAIL);
      setUserType(location.state.userType as RESSELLER_USER_TYPE);
      if (location.state.userType === RESSELLER_USER_TYPE.SALESFORCE_USER) {
        setResellerInfo({
          companyName: location.state.companyName as string,
          name: location.state.name as string,
          cppNo: location.state.cppNo as string,
        });
      }
    }
  }, [location.state?.userType, resellerInfo]);
  return (
    <ResellerPresenter
      loginInfo={loginInfo}
      isValidated={isValidated}
      validateMsg={validateMsg}
      onChangeLoginInfo={onChangeLoginInfo}
      handleClick={handleClick}
      onClickGoBack={onClickGoBack}
      type={type}
      onClickSignUp={onClickSignUp}
      userType={userType}
      resellerInfo={resellerInfo}
    />
  );
}
